import { useEffect, useState } from 'react'

export const useKeyboardHeight = (): number => {
  const [height, setHeight] = useState<number>(0)

  useEffect(() => {
    if (typeof window === 'undefined') return

    let isSubscribed = true
    let removePluginListeners: (() => void) | null = null
    let removeViewportListeners: (() => void) | null = null
    let rafId: number | null = null

    const updateHeight = (nextHeight: number) => {
      const sanitized = Math.max(0, Math.round(nextHeight))
      if (isSubscribed) {
        setHeight((prev) => (prev === sanitized ? prev : sanitized))
      }
    }

    const installViewportFallback = () => {
      if (!isSubscribed) return
      const onViewportChange = () => {
        if (rafId !== null) cancelAnimationFrame(rafId)
        rafId = requestAnimationFrame(() => {
          if (!window.visualViewport) return
          const inset = Math.max(0, window.innerHeight - window.visualViewport.height)
          updateHeight(inset > 50 ? inset : 0)
        })
      }

      const viewport = window.visualViewport
      if (viewport) {
        viewport.addEventListener('resize', onViewportChange)
        viewport.addEventListener('scroll', onViewportChange)
        removeViewportListeners = () => {
          viewport.removeEventListener('resize', onViewportChange)
          viewport.removeEventListener('scroll', onViewportChange)
        }
      }
    }

    const init = async () => {
      try {
        const { Keyboard } = await import('@capacitor/keyboard')
        if (!isSubscribed) return

        const willShow = await Keyboard.addListener('keyboardWillShow', (info) => {
          updateHeight(info.keyboardHeight)
        })
        const didShow = await Keyboard.addListener('keyboardDidShow', (info) => {
          updateHeight(info.keyboardHeight)
        })
        const willHide = await Keyboard.addListener('keyboardWillHide', () => {
          updateHeight(0)
        })
        const didHide = await Keyboard.addListener('keyboardDidHide', () => {
          updateHeight(0)
        })

        removePluginListeners = () => {
          void willShow.remove()
          void didShow.remove()
          void willHide.remove()
          void didHide.remove()
        }
      } catch {
        // Fallback to visualViewport for web/desktop when plugin is absent or fails
        installViewportFallback()
      }
    }

    void init()

    return () => {
      isSubscribed = false
      if (rafId !== null) cancelAnimationFrame(rafId)
      if (removePluginListeners) removePluginListeners()
      if (removeViewportListeners) removeViewportListeners()
    }
  }, [])

  return height
}

export const KeyboardHeightBridge = (): null => {
  const keyboardHeight = useKeyboardHeight()
  const isOpen = keyboardHeight > 0

  useEffect(() => {
    if (typeof document === 'undefined') return
    document.documentElement.style.setProperty('--keyboard-height', `${keyboardHeight}px`)
    return () => {
      document.documentElement.style.removeProperty('--keyboard-height')
    }
  }, [keyboardHeight])

  useEffect(() => {
    if (typeof document === 'undefined') return
    if (isOpen) {
      document.body.classList.add('keyboard-open')
      document.body.setAttribute('data-keyboard-open', 'true')
    } else {
      document.body.classList.remove('keyboard-open')
      document.body.setAttribute('data-keyboard-open', 'false')
    }
    return () => {
      document.body.classList.remove('keyboard-open')
      document.body.removeAttribute('data-keyboard-open')
    }
  }, [isOpen])

  return null
}

